"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  label: "ENERGIE-EFFIZIENZ",
  value: 85,
  suffix: "%",
  description: "Höhere Effizienz im Vergleich zu herkömmlichen Heizsystemen."
}, {
  label: "PLATZSPAREND",
  value: 100,
  prefix: "",
  suffix: "%",
  description: "Vollständig verborgen im Dachboden."
}, {
  label: "ZUFRIEDENE KUNDEN",
  value: 98,
  suffix: "%",
  description: "Hausbesitzer, die unser flüsterleises System weiterempfehlen."
}, {
  label: "JAHRE ERFAHRUNG",
  value: 15,
  suffix: "+",
  description: "Langjährige Expertise in der modernen Klimatechnik."
}];
const comparisons = [{
  label: "GERÄUSCHPEGEL",
  beam: 15,
  traditional: 100
}, {
  label: "SICHTBARKEIT",
  beam: 0,
  traditional: 100
}, {
  label: "HEIZLEISTUNG",
  beam: 95,
  traditional: 80
}, {
  label: "INSTALLATIONSZEIT",
  beam: 40,
  traditional: 100
}];
function AnimatedNumber({
  value,
  prefix = "",
  suffix = "",
  isVisible
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  isVisible: boolean;
}) {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;
    const duration = 1500;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * value));
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setDisplay(value);
      }
    };
    requestAnimationFrame(tick);
  }, [isVisible, value]);
  return <span className="font-default font-bold leading-none tracking-tighter" style={{
    fontSize: "clamp(6rem, 12vw, 10rem)",
    color: "transparent",
    WebkitTextStroke: "2px var(--primary)"
  }}>
      {prefix}
      {display}
      {suffix}
    </span>;
}
function ComparisonBar({
  label,
  beam,
  traditional,
  isVisible,
  index
}: {
  label: string;
  beam: number;
  traditional: number;
  isVisible: boolean;
  index: number;
}) {
  return <div className="flex flex-col gap-3" data-index={index}>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-xs tracking-widest text-foreground/70">
          {label}
        </span>
        <span className="font-mono text-xs tracking-widest text-foreground/40">
          DUCTED AC SYSTEM VS HERKÖMMLICH
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-widest text-primary w-20 shrink-0">
            DUCTED AC SYSTEM
          </span>
          <div className="relative h-2 flex-1 bg-foreground/10">
            <div className="absolute inset-y-0 left-0 bg-primary transition-all duration-1000 ease-out" style={{
            width: isVisible ? `${beam}%` : "0%",
            transitionDelay: `${index * 100}ms`
          }} />
          </div>
          <span className="font-mono text-xs text-foreground w-10 text-right tabular-nums">
            {beam}%
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-widest text-foreground/50 w-20 shrink-0">
            HERKÖMML.
          </span>
          <div className="relative h-2 flex-1 bg-foreground/10">
            <div className="absolute inset-y-0 left-0 bg-foreground/40 transition-all duration-1000 ease-out" style={{
            width: isVisible ? `${traditional}%` : "0%",
            transitionDelay: `${index * 100 + 150}ms`
          }} />
          </div>
          <span className="font-mono text-xs text-foreground/60 w-10 text-right tabular-nums">
            {traditional}%
          </span>
        </div>
      </div>
    </div>;
}
export default function Facts() {
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: chartRef,
    isVisible: chartVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4744" id="zahlen-und-fakten" className="bg-background text-foreground py-24 md:py-40 lg:py-[200px] px-6 md:px-12 lg:px-16">
      <div className="max-w-[1600px] mx-auto">
        {}
        <div className="flex flex-col gap-6 mb-16 md:mb-24">
          <span className="font-mono text-xs md:text-sm tracking-widest text-primary">
            KAPITEL 02 &mdash; FAKTEN
          </span>
          <h2 className="font-default font-bold leading-[0.85] tracking-tighter text-foreground" style={{
          fontSize: "clamp(3rem, 9vw, 7rem)"
        }}>
            KLIMATISIERUNG
            <br />
            NEU GEDACHT.
          </h2>
          <div className="h-px w-full bg-foreground/15 mt-4" />
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 relative">
          {metrics.map((metric, i) => <div key={metric.label} data-index={i} className="relative group" style={{
          transitionDelay: `${i * 100}ms`
        }}>
              {}
              {i > 0 && <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-px bg-foreground/15" />}

              <div className="relative p-6 md:p-8">
                {}
                <div className="absolute inset-0 bg-primary -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2" style={{
              transform: "translate(12px, 12px)"
            }} aria-hidden="true" />

                {}
                <div className="relative flex flex-col gap-6 p-6 md:p-8 min-h-[340px]" style={{
              background: "color-mix(in oklab, var(--foreground) 5%, transparent)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in oklab, var(--foreground) 12%, transparent)"
            }}>
                  <span className="font-mono text-[10px] md:text-xs tracking-widest text-foreground/70">
                    {metric.label}
                  </span>

                  <div className="flex-1 flex items-center justify-center py-4">
                    <AnimatedNumber value={metric.value} prefix={metric.prefix} suffix={metric.suffix} isVisible={gridVisible} />
                  </div>

                  <p className="font-serif italic text-sm md:text-base leading-relaxed text-foreground/80">
                    {metric.description}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div ref={chartRef} className="mt-24 md:mt-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs tracking-widest text-primary">
                EFFIZIENZ-VERGLEICH
              </span>
              <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight">
                DUCTED AC SYSTEM VS HERKÖMMLICHE SYSTEME
              </h3>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="block w-4 h-2 bg-primary" />
                <span className="font-mono text-xs tracking-widest text-foreground/80">
                  DUCTED AC SYSTEM
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="block w-4 h-2 bg-foreground/40" />
                <span className="font-mono text-xs tracking-widest text-foreground/60">
                  HERKÖMMLICH
                </span>
              </div>
            </div>
          </div>

          <div className="h-px w-full bg-foreground/15 mb-10" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {comparisons.map((c, i) => <ComparisonBar key={c.label} label={c.label} beam={c.beam} traditional={c.traditional} isVisible={chartVisible} index={i} />)}
          </div>

          <div className="h-px w-full bg-foreground/15 mt-12" />

          <div className="flex flex-col md:flex-row justify-between gap-4 mt-6">
            <span className="font-mono text-[10px] tracking-widest text-foreground/50">
              QUELLE: DUCTED AC SYSTEM TECHNISCHE ANALYSE &mdash; 2024
            </span>
            <span className="font-mono text-[10px] tracking-widest text-foreground/50">
              BASIEREND AUF DURCHSCHNITTSWERTEN
            </span>
          </div>
        </div>
      </div>
    </section>;
}